import styled from '@emotion/styled';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { useState } from 'react';
import Axios from 'axios';

import { breakPoints } from 'constant/breakPoints';
import { CONTACT_EMAIL, JOB_OPTIONS, PHONE_REGEX } from 'constant/appConstant';

import TextInput from 'components/_common/Input/TextInput';
import TextArea from 'components/_common/Input/TextArea';
import Select from 'components/_common/Select/Select';
import { EclipseLinearBlur } from 'styles/styled';

const CONTACT_US_INITIAL_VALUE = {
  email: '',
  company: '',
  name: '',
  phone: '',
  website: '',
  lookingFor: undefined,
  message: '',
};

function ContactUsForm(): JSX.Element {
  const [isShowThankYou, setIsShowThankYou] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const handleOnSubmit = async (
    values: typeof CONTACT_US_INITIAL_VALUE,
    helper: FormikHelpers<typeof CONTACT_US_INITIAL_VALUE>,
  ) => {
    let isValid = true;

    // name REQUIRED
    if (!values.name.trim()) {
      helper.setFieldError('name', 'Your name is required');
      isValid = false;
    }

    // email REQUIRED
    if (!values.email) {
      helper.setFieldError('email', 'Email is required');
      isValid = false;
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)
    ) {
      helper.setFieldError('email', 'Invalid email');
      isValid = false;
    }

    // company REQUIRED
    if (!values.company.trim()) {
      helper.setFieldError('company', 'Company name is required');
      isValid = false;
    }

    // lookingFor REQUIRED
    if (!values.lookingFor) {
      helper.setFieldError('lookingFor', 'Job position is required');
      isValid = false;
    }

    // phone
    if (values.phone.trim() && !PHONE_REGEX.test(values.phone)) {
      helper.setFieldError('phone', 'Invalid phone number');
      isValid = false;
    }

    if (!isValid) {
      return Promise.reject();
    }

    setIsSending(true);

    await Axios.post('/api/contact', {
      ...values,
    });

    setIsSending(false);
    setIsShowThankYou(true);

    return Promise.resolve();
  };

  return (
    <Wrap className="layout-wrap">
      <EclipseLinearBlur width="17.8vw" top="105px" left="-10vw" />

      <EclipseLinearBlur
        width="15.27vw"
        top="20vw"
        right="-10vw"
        rotate={174.72}
      />

      {isShowThankYou ? (
        <ThankYou className="text-center">
          <h6>Thank you!</h6>
          <span>
            Your request was submitted successfully. We will contact you
            shortly.
          </span>

          <p className="text-gray2">
            For any further inquiries, please contact
            <br />
            <a href={`mailto:${CONTACT_EMAIL.HREF}`}>
              <b className="text-yellow">{CONTACT_EMAIL.LABEL}</b>
            </a>
          </p>
        </ThankYou>
      ) : (
        <>
          <Title>
            <h2 className="font-semibold">
              We are here to make your imagination become{' '}
              <b className="text-yellow">a real thing</b>.
            </h2>
            <p className="font-medium">Let’s work together!</p>
          </Title>

          <Formik
            initialValues={CONTACT_US_INITIAL_VALUE}
            onSubmit={handleOnSubmit}
          >
            {({ isSubmitting, values, errors }) => (
              <StyledForm>
                <div className="form-row">
                  <div className="form-row-item item-50">
                    <Field
                      id="name"
                      name="name"
                      label="Your Name"
                      required
                      as={TextInput}
                    />
                  </div>

                  <div className="form-row-item item-50">
                    <Field
                      id="company"
                      name="company"
                      label="Company"
                      required
                      as={TextInput}
                    />
                  </div>

                  <div className="form-row-item item-50">
                    <Field
                      id="email"
                      name="email"
                      label="Email"
                      type="email"
                      required
                      as={TextInput}
                    />
                  </div>

                  <div className="form-row-item item-50">
                    <Field
                      id="phone"
                      name="phone"
                      label="Phone"
                      as={TextInput}
                    />
                  </div>

                  <div className="form-row-item item-50">
                    <Field
                      id="website"
                      name="website"
                      label="Website"
                      as={TextInput}
                    />
                  </div>

                  <div className="form-row-item item-50">
                    <Field
                      name="lookingFor"
                      label="What are you looking for?"
                      isSearchable={false}
                      required
                      component={Select}
                      options={JOB_OPTIONS}
                    />
                  </div>

                  <div className="form-row-item">
                    <Field
                      id="message"
                      name="message"
                      label="Leave a message"
                      rows={3}
                      as={TextArea}
                    />
                  </div>
                </div>

                <ErrorMessage>
                  {Object.values(errors).map((errMsg) => (
                    <p key={errMsg}>{errMsg}</p>
                  ))}
                </ErrorMessage>

                <Button
                  type="submit"
                  aria-label="Submit"
                  disabled={
                    isSending ||
                    isSubmitting ||
                    !values.company ||
                    !values.name ||
                    !values.lookingFor ||
                    !values.email
                  }
                >
                  Submit
                </Button>
              </StyledForm>
            )}
          </Formik>
        </>
      )}
    </Wrap>
  );
}

export default ContactUsForm;

const Wrap = styled.section`
  position: relative;
  overflow: hidden;

  @media screen and (min-width: ${breakPoints.desktopS}px) {
    position: static;
    overflow: visible;
  }
`;

const Title = styled.div`
  max-width: 933px;
  z-index: 10;
  position: relative;

  h2 {
    font-size: 32px;
    line-height: 38px;
    letter-spacing: -0.005em;
    margin: 102px 0 8px;
  }

  p {
    margin-bottom: 32px;
    font-size: 16px;
  }

  @media screen and (min-width: ${breakPoints.tabletM}px) {
    h2 {
      font-size: 48px;
      line-height: 58px;
      margin: 172px 0 16px;
    }
  }
`;

const ThankYou = styled.div`
  max-width: 343px;
  width: 100%;
  margin: 0 auto;

  h6 {
    font-weight: 600;
    font-size: 40px;
    color: #b660e1;
    margin: 110px 0 8px;
  }

  span {
    font-weight: 500;
    font-size: 18px;
  }

  p {
    font-size: 16px;
    margin-top: 24px;
  }

  @media screen and (min-width: ${breakPoints.tabletM}px) {
    max-width: 500px;

    h6 {
      font-size: 64px;
      margin-bottom: 24px;
    }

    span {
      margin-top: 48px;
      font-size: 24px;
    }

    p {
      font-size: 18px;
      margin-top: 48px;
    }
  }
`;

const StyledForm = styled(Form)`
  textarea {
    resize: none;
  }

  @media screen and (min-width: ${breakPoints.tabletM}px) {
    .form-row {
      display: flex;
      flex-wrap: wrap;
      margin: 0 -20px 0;

      &-item {
        width: 100%;
        padding: 0 20px;

        &.item-50 {
          max-width: 50%;
          flex: 0 0 50%;
        }
      }
    }
  }
`;

const Button = styled.button`
  background: linear-gradient(
    188.34deg,
    #a336d9 13.45%,
    rgba(163, 54, 217, 0.6) 144.25%
  );
  color: var(--white);
  font-weight: 500;
  border-radius: 100px;
  width: 100%;
  max-width: 400px;
  border: 0;
  line-height: 52px;
  font-size: 16px;
  border: 2px solid var(--purple-darken);
  margin-top: 32px;

  &:hover,
  &:not(:disabled):hover {
    border-color: var(--white);
    background: transparent;
  }

  &:disabled,
  &[disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media screen and (min-width: ${breakPoints.tabletM}px) {
    max-width: 206px;
    margin-top: 24px;
  }
`;

const ErrorMessage = styled.div`
  color: #ca4a4a;
  font-size: 14px;
`;
